import { ProductType } from '../type-aliases/product-type';

export type DisplayInfoMap = {
  [K in ProductType]?: PlatformSpecificDisplayInfo;
};

export interface PlatformSpecificDisplayInfo {
  title: string; // the display name of the platform (doesn't always equal the name of the plan)
  checkoutImageClass: string;
  description: string;
  featureText: string;
  class: string; // css classes are based off of this field
  color?: string; // scss var name of associated color in the design system; use the class property instead if you can ^^
  iconClass: string; // css class that references platform font-icon
  coloredIcon: string;
  grayIcon?: string;
  unitOfMeasure: string; // month, agent, etc
  checkoutRemovable?: boolean; // for temp purposes, it's not expected that any plans should be non-removable
  visibleToNonSAUser?: boolean; // for temp purposes. this should only be used for cards in-progress
  sortOrder: number;
  platform: ProductType; // a little redundancy to help avoid weirdness in map operations
}

export interface PlanDisplayInfo {
  usageInfo: string[];
  unitPrice: number;
}

export interface PlanDisplayIcon {
  iconClass: string;
  active: boolean;
}

/**
 * This map contains display information about a platform.
 * This info is NOT dependent on a specific plan.
 * This info will be the same for every plan of the same platform
 */
export const platformDisplayInfo: DisplayInfoMap = {
  calls: {
    title: 'Call Tracking',
    class: 'calls',
    color: 'attribluetion',
    iconClass: 'icon-call-tracking',
    checkoutImageClass: 'background--brand-abstract13',
    // eslint-disable-next-line max-len
    description:
      'Discover how people hear about your business, what makes them pick up the phone, and how to get more customers on the line.',
    featureText: '',
    coloredIcon: 'billing/upsell-card/calls.svg',
    unitOfMeasure: 'month',
    checkoutRemovable: false,
    visibleToNonSAUser: true,
    sortOrder: 1,
    platform: 'calls',
  },
  voice_assist: {
    title: 'Voice Assist',
    class: 'va',
    color: 'plum',
    iconClass: 'icon-voice-assist',
    checkoutImageClass: 'background--brand-abstract21',
    description:
      'Convert every lead with an AI assistant that answers, captures, and qualifies inbound calls.',
    featureText:
      'Caller intake, text message for appointment links and call transfers',
    coloredIcon: '',
    grayIcon: '',
    unitOfMeasure: 'month',
    checkoutRemovable: true,
    visibleToNonSAUser: true,
    sortOrder: 2,
    platform: 'voice_assist',
  },
  forms: {
    title: 'Form Tracking',
    class: 'forms',
    color: 'sunflower',
    iconClass: 'icon-form-tracking',
    checkoutImageClass: 'background--brand-abstract10',
    // eslint-disable-next-line max-len
    description:
      'Get full visibility into the end-to-end customer journey by connecting your ads, campaigns, and keywords to form submissions.',
    featureText: 'Leads Report and Multi-Touch Cost per Lead Report',
    coloredIcon: 'billing/upsell-card/forms.svg',
    grayIcon: 'billing/upsell-card/no-forms.svg',
    unitOfMeasure: 'month',
    checkoutRemovable: true,
    visibleToNonSAUser: true,
    sortOrder: 5,
    platform: 'forms',
  },
  conversation_intelligence: {
    title: 'Conversation Intelligence',
    class: 'ci',
    color: 'vin-rouge',
    iconClass: 'icon-conversation-intelligence',
    checkoutImageClass: 'background--brand-abstract11',
    description:
      "Get your recorded calls transcribed and analyzed so you can quickly understand what's happening on your calls.",
    featureText: 'Automation Rules feature',
    coloredIcon: 'billing/upsell-card/ci.svg',
    grayIcon: 'billing/upsell-card/no-ci.svg',
    unitOfMeasure: 'month',
    checkoutRemovable: true,
    visibleToNonSAUser: true,
    sortOrder: 3,
    platform: 'conversation_intelligence',
  },
  convert_assist: {
    title: 'Convert Assist',
    class: 'ca',
    color: 'plum',
    iconClass: 'icon-convert-assist',
    checkoutImageClass: 'background--brand-abstract20',
    description:
      'Turn your conversations with leads into actionable next steps, ready-to-use follow-up messages, and coaching to help you convert.',
    featureText:
      'Action plan, smart follow-up, and coaching with unlimited generated responses',
    coloredIcon: 'billing/upsell-card/ca.svg',
    grayIcon: 'billing/upsell-card/no-ca.svg',
    unitOfMeasure: 'month',
    checkoutRemovable: true,
    visibleToNonSAUser: true,
    sortOrder: 4,
    platform: 'convert_assist',
  },
  lead_center: {
    title: 'Lead Center',
    class: 'lead-center',
    color: 'ringo-mint',
    iconClass: 'icon-lead-center',
    checkoutImageClass: 'background--brand-abstract12',
    // eslint-disable-next-line max-len
    description:
      'Take and make calls and texts directly from your CallRail tracking numbers, and manage all your communications from a single, unified inbox.',
    featureText: 'Call transfers and monitoring',
    coloredIcon: 'billing/upsell-card/lead-center.svg',
    grayIcon: 'billing/upsell-card/no-lead-center.svg',
    unitOfMeasure: 'agent',
    checkoutRemovable: true,
    visibleToNonSAUser: true,
    sortOrder: 6,
    platform: 'lead_center',
  },
};

// It's possible that we have more platforms that come back in the subscription keys than are valid
// so we need some local sense of what all the possible platforms are currently
export const ACTIVE_PLATFORMS: ProductType[] = [
  'calls',
  'voice_assist',
  'conversation_intelligence',
  'convert_assist',
  'forms',
  'lead_center',
];

/**
 * returns the `ACTIVE_PLATFORMS` but sorted base on our UI ordering
 */
export const sortedPlatforms = () => {
  return ACTIVE_PLATFORMS.sort((a: ProductType, b: ProductType) => {
    return platformDisplayInfo[a].sortOrder - platformDisplayInfo[b].sortOrder;
  });
};
